/**
 * ItemSearchInput.jsx — shared search-input component used by Price Check
 * and Item Analysis (issue #94). Single source of truth for:
 *
 *   • debounce window (default 250ms — see issue #29)
 *   • decoupled raw / debounced signals so the input keeps focus while
 *     the parent re-renders on each fetch
 *   • clear ('✖') button
 *   • optional onSelect / onSubmit hook for Enter key
 *   • optional onArrow callback so a parent can drive a suggestions
 *     dropdown with the same keyboard semantics across widgets
 *
 * Usage:
 *   const [q, qDebounced] = createSignal('');
 *   <ItemSearchInput
 *     initialValue={...}
 *     onChange={setRawAndScheduleDebounce}
 *     onSubmit={(v) => pickItem(v)}
 *   />
 *
 * To keep parity, both widgets construct one of these and bind the
 * debounced value into their useWidgetData() params.
 */

import { createSignal, Show, onCleanup } from 'solid-js';

const DEFAULT_DEBOUNCE_MS = 250;

/**
 * @param props.initialValue   {string}            initial input text
 * @param props.placeholder    {string}            placeholder text
 * @param props.debounceMs     {number}            override debounce (default 250)
 * @param props.onValue        {(raw, debounced) => void}  fires with both raw + debounced values
 * @param props.onSubmit       {(value) => void}   Enter key
 * @param props.onArrowDown    {() => void}        ArrowDown — for parent dropdown nav
 * @param props.onArrowUp      {() => void}        ArrowUp — for parent dropdown nav
 * @param props.onEscape       {() => void}        Escape key
 * @param props.class          {string}            extra CSS class on wrapper
 * @param props.inputClass     {string}            extra CSS class on the <input>
 */
export default function ItemSearchInput(props) {
  const [raw, setRaw] = createSignal(props.initialValue || '');
  let timer = 0;

  function emit(rawVal, debouncedVal) {
    if (typeof props.onValue === 'function') props.onValue(rawVal, debouncedVal);
  }
  // Initial debounced echo so parents that mount with a non-empty value
  // get a fetch trigger.
  emit(raw(), raw());

  function schedule(v) {
    clearTimeout(timer);
    const ms = Number.isFinite(props.debounceMs) ? props.debounceMs : DEFAULT_DEBOUNCE_MS;
    timer = setTimeout(() => emit(v, v), ms);
  }
  onCleanup(() => clearTimeout(timer));

  function clear() {
    clearTimeout(timer);
    setRaw('');
    emit('', '');
  }

  function handleInput(e) {
    const v = e.currentTarget.value;
    setRaw(v);
    // Echo raw immediately (parent can show a "typing…" hint), then
    // schedule the debounced trigger.
    emit(v, undefined);
    schedule(v);
  }

  function handleKey(e) {
    if (e.key === 'Enter') {
      // On Enter, flush the debounce and call onSubmit with the current
      // raw value so the action is responsive.
      clearTimeout(timer);
      emit(raw(), raw());
      if (typeof props.onSubmit === 'function') props.onSubmit(raw());
    } else if (e.key === 'ArrowDown') {
      if (typeof props.onArrowDown === 'function') {
        e.preventDefault();
        props.onArrowDown();
      }
    } else if (e.key === 'ArrowUp') {
      if (typeof props.onArrowUp === 'function') {
        e.preventDefault();
        props.onArrowUp();
      }
    } else if (e.key === 'Escape') {
      if (typeof props.onEscape === 'function') props.onEscape();
    }
  }

  return (
    <div class={`v2-item-search ${props.class || ''}`}>
      <input
        class={`v2-input v2-item-search__input ${props.inputClass || ''}`}
        type="search"
        placeholder={props.placeholder || 'Search items…'}
        value={raw()}
        onInput={handleInput}
        onKeyDown={handleKey}
        autocomplete="off"
        spellcheck={false}
      />
      <Show when={raw()}>
        <button class="v2-btn-sm v2-item-search__clear"
          onClick={clear} title="Clear" type="button">✖</button>
      </Show>
    </div>
  );
}
